import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import { Dialog } from "primereact/dialog";
import Langs from "../../app/lang/langs";
import { showErrorToast } from "../../app/utils/helpers"
import {
    addCollabMission,
    editCollabMission,
    getCollabMissions
} from '../../components/teams/teamsSlice'
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import moment from 'moment'
import styles from './CollabsView.module.css'

export default function AddMissionModal(props) {

    const dispatch = useDispatch()

    const isMobile = useSelector((state) => state.settings.isMobile)
    const postes = useSelector((state) => state.collabs.postes)
    const teams = useSelector((state) => state.teams.list)
    const [newMission, setNewMission] = useState({})

    // the contract the mission is attached to: the ongoing one, or the upcoming one
    // when the collab is hired for a future date. A contract that has not started yet
    // bounds the mission from below — the mission cannot begin before the contract does
    const contractStartBound = () => {
        const entreeDate = props.actualContract?.entree_date
        if (!entreeDate || !moment(entreeDate).isAfter(moment(), "day")) return null
        return moment(entreeDate).startOf("day").toDate()
    }

    // when the contract has an end date, the mission must fit inside the contract
    // period: start before the contract ends, and an end date set on or before the
    // contract end date
    const validateAgainstContract = () => {
        const contractStartDate = contractStartBound()
        if (contractStartDate && moment(newMission.start_date).isBefore(moment(contractStartDate), "day")) {
            showErrorToast("MISSION_START_BEFORE_CONTRACT_START")
            return false
        }
        const contractEndDate = props.actualContract?.sortie_date
        if (!contractEndDate) return true
        if (moment(newMission.start_date).isAfter(moment(contractEndDate), "day")) {
            showErrorToast("MISSION_START_AFTER_CONTRACT_END")
            return false
        }
        if (!newMission.end_date || moment(newMission.end_date).isAfter(moment(contractEndDate), "day")) {
            showErrorToast("MISSION_END_REQUIRED_BEFORE_CONTRACT_END")
            return false
        }
        return true
    }

    const handleAddMission = async () => {
        if (!validateAgainstContract()) return
        try {
            const actionResponse = await dispatch(
                addCollabMission({
                    id_collab: props.collabDetails.id,
                    id_team: newMission.id_team,
                    id_poste: newMission.id_poste,
                    start_date: newMission.start_date ? moment(newMission.start_date).format("YYYY-MM-DD HH:mm:ss") : null,
                    end_date: newMission.end_date ? moment(newMission.end_date).format("YYYY-MM-DD HH:mm:ss") : null,
                })
            ).unwrap()
            if (actionResponse.responseData?.error == "conflict") {
                showErrorToast("MISSION_CONFLICT_ERROR")
            } else if (actionResponse.responseData?.status) {
                dispatch(getCollabMissions({ id_collab: props.collabDetails.id }))
                props.onHide()
                setNewMission({})
            }
        } catch (error) {
            console.error("Error adding mission:", error)
            showErrorToast()
        }
    }

    const handleStartDateChange = (e) => {
        const selectedDate = e.value ? new Date(e.value) : null
        setNewMission((prev) => ({
            ...prev,
            start_date: selectedDate
        }))
    }

    const handleEndDateChange = (e) => {
        const selectedDate = e.value ? new Date(e.value) : null
        setNewMission((prev) => ({
            ...prev,
            end_date: selectedDate
        }))
    }

    const isFormValid = () => {
        return (
            newMission.id_poste &&
            newMission.id_team &&
            newMission.start_date
        )
    }

    const footerView = () => {
        return <Row className="py-3 d-flex justify-content-space-center">
            <Col lg={4}></Col>
            <Col lg={4} className="d-flex justify-content-between">
                <PrimaryButton
                    onClick={handleAddMission}
                    className=""
                    title={<Langs str="ADD" />}
                    color="green"
                    active={isFormValid()}
                />
            </Col>
            <Col lg={4}></Col>
        </Row>
    }

    return (
        <Dialog
            visible={props.show}
            style={{ width: isMobile === 2 ? "90%" : "50%" }}
            onHide={() => {
                props.onHide()
                setNewMission({})
            }}
            header={<Langs str="ADD_MISSION" />}
            footer={footerView()}
            draggable={false}
            dismissableMask={false}
        >
            <Row className="py-3 d-flex justify-content-space-between">
                <Col lg={3} className="mb-1 d-flex align-items-center fw-bold">
                    <label className="fw-bold"><Langs str="CLIENT" /></label>
                </Col>
                <Col lg={9} className="mb-1 pe-3">
                    <Dropdown
                        className={styles.dropdown}
                        filter
                        id="id_team"
                        value={newMission.id_team}
                        options={teams}
                        onChange={(e) =>
                            setNewMission((prev) => ({
                                ...prev,
                                id_team: e.value,
                            }))
                        }
                        placeholder="Sélectionner un client"
                    />
                </Col>
            </Row>
            <Row className="py-3 d-flex justify-content-space-between">
                <Col lg={3} className="mb-1 d-flex align-items-center fw-bold">
                    <label className="fw-bold"><Langs str="POSTE" /></label>
                </Col>
                <Col lg={9} className="mb-1 pe-3">
                    <Dropdown
                        className={styles.dropdown}
                        filter
                        id="poste_name"
                        options={postes}
                        value={newMission.id_poste}
                        showOnFocus={false}
                        onChange={(e) =>
                            setNewMission((prev) => ({
                                ...prev,
                                id_poste: e.value
                            }))
                        }
                        placeholder="Nom du poste"
                    />
                </Col>
            </Row>

            <Row className="p-fluid py-3 d-flex justify-content-space-between">
                <Col lg={3} className="mb-1 d-flex align-items-center fw-bold">
                    <label className="fw-bold"><Langs str="START_DATE" /></label>
                </Col>
                <Col lg={9} className="mb-1 pe-3">
                    <Calendar
                        id="start_date"
                        value={newMission.start_date}
                        onChange={handleStartDateChange}
                        dateFormat="dd/mm/yy"
                        showIcon
                        minDate={contractStartBound()}
                        maxDate={props.actualContract?.sortie_date ? new Date(props.actualContract.sortie_date) : null}
                    />
                </Col>
            </Row>
            <Row className="p-fluid py-3 d-flex justify-content-space-between">
                <Col lg={3} className="mb-1 d-flex align-items-center fw-bold">
                    <label className="fw-bold">
                        <Langs str="END_DATE" />
                    </label>
                </Col>
                <Col lg={9} className="mb-1 pe-3">
                    <Calendar
                        id="end_date"
                        value={newMission.end_date}
                        onChange={handleEndDateChange}
                        dateFormat="dd/mm/yy"
                        showIcon
                        disabled={!newMission.start_date}
                        minDate={newMission.start_date ? new Date(newMission.start_date) : contractStartBound()}
                        maxDate={props.actualContract?.sortie_date ? new Date(props.actualContract.sortie_date) : null}
                    />
                </Col>
            </Row>
        </Dialog>
    )
}